import { useState, FormEvent } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    units: '',
    message: '',
    transactionalConsent: false,
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('propertyAddress', formData.propertyAddress);
      formDataToSend.append('units', formData.units);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('transactionalConsent', formData.transactionalConsent ? 'Yes' : 'No');
      formDataToSend.append('marketingConsent', formData.marketingConsent ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d5rkocetc768qf4bqkkg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          units: '',
          message: '',
          transactionalConsent: false,
          marketingConsent: false,
        });
        
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setIsSubmitting(false);
        setSubmitStatus('error');
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  return (
    <section id="contact-section" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Get Started
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Ready to Sell My Apartments Fast?
            </h2>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Get a fair cash offer from experienced apartment buyers with a proven track record. We close through title companies in 7-14 days and handle all the details.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900 mb-1">Call Us</div>
                  <a href="tel://+19152065814" className="text-xl text-teal-600 hover:text-teal-700 font-bold cursor-pointer">
                    (915) 206-5814
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900 mb-1">Email Us</div>
                  <a href="mailto:ElPaso@trexrealestate.com" className="text-lg text-teal-600 hover:text-teal-700 cursor-pointer">
                    ElPaso@trexrealestate.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-fill text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900 mb-1">Visit Us</div>
                  <div className="text-gray-700">
                    3016 Gull Lake Pl<br />
                    El Paso, TX 79936
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Why Choose T-Rex Real Estate?</h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-200"></i>
                  <span>150+ apartment buildings purchased</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-200"></i>
                  <span>$85M+ in total transactions</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-200"></i>
                  <span>100% title company closings</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-checkbox-circle-fill text-teal-200"></i>
                  <span>7-14 day average closing time</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Cash Offer Today</h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-start space-x-3">
                <i className="ri-checkbox-circle-fill text-teal-600 text-2xl flex-shrink-0"></i>
                <div>
                  <div className="font-bold text-teal-900">Thank You!</div>
                  <div className="text-teal-700">We'll contact you within 24 hours to discuss your property.</div>
                </div>
              </div>
            )}

            <form id="sell-apartments-contact" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="(915) 555-0123"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Address *
                </label>
                <input
                  type="text"
                  name="propertyAddress"
                  required
                  value={formData.propertyAddress}
                  onChange={(e) => setFormData({...formData, propertyAddress: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="123 Main St, El Paso, TX"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Units *
                </label>
                <select
                  name="units"
                  required
                  value={formData.units}
                  onChange={(e) => setFormData({...formData, units: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select number of units</option>
                  <option value="2-4">2-4 units</option>
                  <option value="5-12">5-12 units</option>
                  <option value="13-30">13-30 units</option>
                  <option value="31-50">31-50 units</option>
                  <option value="50+">50+ units</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your property, condition, tenant situation, timeline, etc. (500 characters max)"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="apartments-transactionalConsent"
                    name="transactionalConsent"
                    required
                    checked={formData.transactionalConsent}
                    onChange={(e) => setFormData({ ...formData, transactionalConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="apartments-transactionalConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                    <span className="text-red-500">*</span> I consent to receive transactional messages from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="apartments-marketingConsent"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="apartments-marketingConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                    I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg font-bold text-lg hover:bg-teal-700 transition-all transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-send-plane-fill mr-2"></i>
                    Get My Cash Offer
                  </span>
                )}
              </button>

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                  Something went wrong. Please call us at (915) 206-5814.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to be contacted about your property. We respect your privacy and will never share your information.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}